import React from 'react';
import { getLabelIcon, getSectionIcon } from '@/components/atoms/IconMap';
import { ICronJobTrackerProps } from '@/pages/CronJobsTracker/cronJobsTracker.interface';
import { getDateTime } from '@/utils/dateTime';
import { NoDataPlate } from '@/components/organism/TableExpand/primitives';
import { SectionCard } from '@/components/organism/Cards/SectionCard';
import { StatCell } from '@/components/organism/Cards/StatCell';
import PillCell from '@/components/organism/StatusPills/PillCell';
import { Text } from '@/components/atoms/AppText';
import { ORGANISM_PROPS } from '@/constant/global/global.constant';
import {
  APP_ACCENT,
  APP_SEMANTIC,
  APP_BORDER,
} from '@/constant/theme/colors.constant';
import { tid } from '@/utils/testId';
import { CRON_JOBS_TEST_IDS as TID } from '@/constant/testIds/cronJobs.testIds';

interface CronJobExpandedPanelProps {
  row: ICronJobTrackerProps;
}

// ─── Success‑rate progress bar ────────────────────────────────────────────────
const SuccessRateBar = ({ pct }: { pct: number }) => {
  const color =
    pct === 100
      ? APP_ACCENT.green.bg
      : pct >= 50
        ? APP_ACCENT.amber.bg
        : APP_ACCENT.red.bg;
  const textColor =
    pct === 100
      ? APP_SEMANTIC.success.text
      : pct >= 50
        ? APP_SEMANTIC.warning.text
        : APP_SEMANTIC.danger.text;
  return (
    <div className="flex items-center gap-2 mt-0.5">
      <div
        className={`flex-1 h-1.5 rounded-full overflow-hidden ${APP_SEMANTIC.neutral.bg}`}
      >
        <div
          className={`h-full rounded-full transition-all duration-500 ${color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={`text-[12px] font-bold tabular-nums ${textColor}`}>
        {pct}%
      </span>
    </div>
  );
};

export const CronJobExpandedPanel = ({ row }: CronJobExpandedPanelProps) => {
  const { metadata, report } = row;

  const total = report?.totalRecords ?? 0;
  const success = report?.successRecords ?? 0;
  const failed = report?.failedRecords ?? 0;
  const pct = total > 0 ? Math.round((success / total) * 100) : 0;

  const method = (metadata?.method ?? '').toUpperCase();

  return (
    <div
      data-testid={tid(TID.EXPANDED_PANEL)}
      className="grid grid-cols-1 md:grid-cols-2 gap-3 p-0.5"
    >
      {/* ── Report card ───────────────────────────────────────────────── */}
      <SectionCard
        testId={tid(TID.EXPANDED_REPORT)}
        title="Report"
        icon={getSectionIcon('Report')}
        compact
        bodyClassName=""
      >
        {total === 0 && success === 0 && failed === 0 ? (
          <NoDataPlate
            message={ORGANISM_PROPS.CRON_JOB_EXPANDED.NO_REPORT_DATA}
          />
        ) : (
          <>
            <div
              className={`grid grid-cols-3 divide-x divide-y ${APP_BORDER.divideSoft}`}
            >
              <StatCell
                label={ORGANISM_PROPS.CRON_JOB_EXPANDED.LABEL_TOTAL}
                labelIcon={getLabelIcon(
                  ORGANISM_PROPS.CRON_JOB_EXPANDED.LABEL_TOTAL,
                )}
                value={
                  <PillCell value={String(total)} shape="badge" size="xs" />
                }
              />
              <StatCell
                label="Success"
                labelIcon={getLabelIcon('Success')}
                value={
                  <PillCell
                    value={String(success)}
                    preset="active"
                    dot
                    size="xs"
                  />
                }
              />
              <StatCell
                label="Failed"
                labelIcon={getLabelIcon('Failed')}
                value={
                  <PillCell
                    value={String(failed)}
                    preset={failed > 0 ? 'inactive' : 'active'}
                    dot
                    size="xs"
                  />
                }
              />
            </div>
            <div className={`px-4 py-3 border-t ${APP_BORDER.soft}`}>
              <Text
                as="p"
                size="sm"
                weight="bold"
                color="subtle"
                tracking="widest"
                className="uppercase mb-1"
              >
                Success Rate
              </Text>
              <SuccessRateBar pct={pct} />
            </div>
          </>
        )}
      </SectionCard>

      {/* ── Metadata card ─────────────────────────────────────────────── */}
      <SectionCard
        testId={tid(TID.EXPANDED_METADATA)}
        title="Metadata"
        icon={getSectionIcon('Metadata')}
        compact
        bodyClassName=""
      >
        {!metadata ? (
          <NoDataPlate message={ORGANISM_PROPS.CRON_JOB_EXPANDED.NO_METADATA} />
        ) : (
          <>
            {metadata.url && (
              <div
                className={`flex items-start gap-3 px-4 py-3 border-b ${APP_BORDER.soft}`}
              >
                <span
                  className={`mt-0.5 ${APP_SEMANTIC.neutral.text} shrink-0`}
                >
                  {getLabelIcon('URL')}
                </span>
                <div className="min-w-0">
                  <Text
                    as="p"
                    size="sm"
                    weight="bold"
                    color="subtle"
                    tracking="widest"
                    className="uppercase mb-0.5"
                  >
                    URL
                  </Text>
                  <Text
                    as="p"
                    size="md"
                    color="soft"
                    leading="snug"
                    className="font-mono break-all"
                  >
                    {metadata.url}
                  </Text>
                </div>
              </div>
            )}
            <div
              className={`grid grid-cols-2 divide-x divide-y ${APP_BORDER.divideSoft}`}
            >
              {metadata.method && (
                <StatCell
                  label={ORGANISM_PROPS.CRON_JOB_EXPANDED.LABEL_METHOD}
                  labelIcon={getLabelIcon(
                    ORGANISM_PROPS.CRON_JOB_EXPANDED.LABEL_METHOD,
                  )}
                  value={<PillCell value={method} shape="badge" size="xs" />}
                />
              )}
              {metadata.clientIp && (
                <StatCell
                  label={ORGANISM_PROPS.CRON_JOB_EXPANDED.LABEL_CLIENT_IP}
                  labelIcon={getLabelIcon(
                    ORGANISM_PROPS.CRON_JOB_EXPANDED.LABEL_CLIENT_IP,
                  )}
                  value={metadata.clientIp}
                  mono
                />
              )}
              {metadata.requestTimestamp && (
                <StatCell
                  label={
                    ORGANISM_PROPS.CRON_JOB_EXPANDED.LABEL_REQUEST_TIMESTAMP
                  }
                  labelIcon={getLabelIcon(
                    ORGANISM_PROPS.CRON_JOB_EXPANDED.LABEL_REQUEST_TIMESTAMP,
                  )}
                  value={getDateTime(metadata.requestTimestamp)}
                />
              )}
            </div>
          </>
        )}
      </SectionCard>
    </div>
  );
};
